<template>
  <SimpleForm ref="form" :disabled="isView" :onSubmit="submit" @done="clearForm">
    <p v-if="!isView" class="span-2 form__title">{{ isEdit ? 'Update Business' : 'Add New Business' }}</p>
    <p v-else class="span-2 form__title">Business Detail</p>

    <v-text-field
        v-model="business.name"
        :readonly="isView"
        :rules="[required('A business name must be provided')]"
        class="span-2"
        dense
        label="Name"
        outlined
    />

    <v-select
        v-model="business.categoryId"
        :items="categories"
        :loading="loadingCategories"
        :readonly="isView"
        :rules="[required('A category must be selected')]"
        :value-comparator="(a, b) => a && b && a === b"
        class="span-2"
        dense
        item-text="name"
        item-value="_id"
        label="Select Category"
        outlined
    >
      <template v-slot:item="{item}">
        <div>
          <v-list-item-content>
            <v-list-item-title>
              {{ item.name }}
            </v-list-item-title>
            <v-list-item-action-text class="text-wrap">
              {{ item.description.length > 50 ? item.description.substring(0, 50) + '...' : item.description }}
            </v-list-item-action-text>
          </v-list-item-content>
        </div>
      </template>
    </v-select>

    <div v-if="!isView" class="mb-3 span-2">
      <phoneNumberField
          v-model="phone"
          :default-country-code="countryCode"
          :error="phoneError"
          :size="$vuetify.breakpoint.xs ? '' : 'lg'"
          class="span-2"
          color="#ee6725"
          error-color="red"
          show-code-on-list
          valid-color="#ee6725"
          @update="validatePhone"
      />
      <p :class="`text-caption ${phoneError && this.showPhoneError ? 'error--text' : 'white--text'} ma-0`">Valid Phone
        is required</p>
    </div>

    <v-text-field
        v-else
        v-model="business.phone"
        :readonly="isView"
        class="span-2"
        dense
        label="Phone Number"
        outlined
    />

    <v-text-field
        v-model="business.website"
        :readonly="isView"
        :rules="[business.website ? url() : true]"
        class="span-2"
        dense
        label="Website"
        outlined
    />

    <v-text-field
        v-model="business.email"
        :readonly="isView"
        :rules="[business.email ? email() : true]"
        class="span-2"
        dense
        label="Email"
        outlined
    />

    <v-combobox
        v-model="business.keywords"
        :readonly="isView"
        :rules="[requiredArray('At least one keyword must be provided')]"
        :search-input.sync="searchKeywords"
        append-icon=""
        chips
        class="span-2"
        clearable
        deletable-chips
        dense
        label="Keywords"
        multiple
        outlined
        small-chips
    >
    </v-combobox>

    <v-row class="span-2">
      <v-col cols="12" md="8">
        <GmapAutocomplete :readonly="isView" :value="business.address" clearable placeholder="Address"
                          @place_changed="setPlace"
                          @keyup.enter="setPlace"/>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
            v-model="location"
            dense
            label="Lng/Lat"
            outlined
            readonly
        />
      </v-col>
    </v-row>

    <div class="span-2">
      <GmapMap
          ref="mapRef"
          :center="center"
          :options="mapOptions"
          :zoom="18"
          map-style-id="roadmap"
          style="width: 100%; height: 50vmin"
          @click="handleMapClick"
      >
        <GmapMarker
            :clickable="true"
            :draggable="true"
            :position="marker.position"
            @click="panToMarker"
            @drag="handleMarkerDrag"
        />
      </GmapMap>
    </div>

    <v-divider class="span-2" style="margin-bottom: 20px"/>

    <div class="mb-3 span-2">
      <h3>Media</h3>

      <div v-if="isView || isEdit" class="mt-2 span-2" style="height: 500px">
        <Carousal :images="this.business.images" class="span-2"/>
      </div>

      <div v-if="!isView && uploadedMediaFiles && uploadedMediaFiles.length > 0" class="my-6">
        <div class="mb-3 span-2">
          <h3>Uploaded Files</h3>
          <v-row class="mt-2 span-2">
            <v-col v-for="(mediaItem, i) in uploadedMediaFiles" :key="i"
                   :style="mediaErrors.length > 0 ? 'border-color: red' : ''"
                   class="file-display ma-2  pa-0"
                   cols="3"
                   no-gutters
                   style="position: relative;max-width: 130px !important;"
            >
              <div class="preview-image">
                <img :src="mediaItem.url" height="100%"
                     width="100%"/>
                <v-icon class="remove-icon" color="error" @click="removeUploadedFiles(mediaItem.fileName, i)">mdi-delete
                </v-icon>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>

      <div v-if="!isView" class="mediaPrickerWrapper py-8 d-flex justify-center align-center my-5 flex-column"
           style="position: relative"
           @click="pickFile"
           @drop="dropHandler($event)"
           @dragenter.prevent
           @dragover.prevent
      >
        <v-avatar color="grey lighten-3 mb-2">
          <v-icon>mdi-upload</v-icon>
        </v-avatar>
        <p class="ma-0 text-center">Drag images here<br>
          <span class="font-weight-bold primary--text">Or select images to upload </span></p>
        <v-file-input
            id="mediaPicker"
            v-model="selectedMedia"
            :value="selectedMedia"
            accept="image/*, video/*"
            class="d-none"
            multiple
            @change="pickHandler"
        />
      </div>

      <div v-if="media.length > 0">
        <v-row class="span-2">
          <v-col v-for="(mediaItem, i) in media" :key="i"
                 :style="mediaErrors.length > 0 ? 'border-color: red' : ''"
                 class="file-display ma-2  pa-0"
                 cols="3"
                 no-gutters
                 style="position: relative;max-width: 130px !important;"
          >
            <div class="preview-image">
              <img :src="mediaItem.url" height="100%"
                   width="100%"/>
              <v-icon class="remove-icon" color="error" @click="removeMedia(mediaItem)">mdi-delete</v-icon>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>

    <confirm-dialogue ref="confirm"/>
  </SimpleForm>
</template>

<script>
import SimpleForm from '../../components/Form';
import {email, required, requiredArray, url} from '../../utils/validators';
import parsePhoneNumber from "libphonenumber-js";
import {BusinessService} from "../../services/business";
import {CategoryService} from "../../services/category";
import Carousal from "../../components/Carousal";
import axios from "axios";

async function uploadWithMessage(context, list, message) {
  context.showLoading(true, message)
  const newList = [];
  for (const item of list) {
    let formData = new FormData()
    formData.append('image', item.file);
    const response = await axios.post('/save-image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        }
    )
    newList.push(response.data.name)
    context.showLoading(true, message)
  }
  return newList;
}

export default {
  name: 'Form',
  components: {SimpleForm, Carousal},

  data: () => ({
    isEdit: false,
    isView: false,
    loading: false,
    loadingCategories: false,
    searchKeywords: '',
    business: {
      name: undefined,
      categoryId: undefined,
      phone: undefined,
      website: undefined,
      email: undefined,
      keywords: [],
      address: undefined,
      images: [],
      location: {
        type: 'Points',
        coordinates: []
      }
    },
    location: '',
    countryCode: 'PK',
    categories: [],
    errors: [],
    phone: undefined,
    phoneError: false,
    showPhoneError: false,
    service: new BusinessService(),
    categoryService: new CategoryService(),
    marker: {position: {lng: 10, lat: 10}},
    center: {lng: 10, lat: 10},
    mapOptions: {
      disableDefaultUI: true,
    },
    currentPlace: null,
    canGetAddress: true,

    // media picker
    media: [],
    mediaErrors: [],
    selectedMedia: [],
    uploadedMediaFiles: [],
    filesToBeDeleted: [],
    uploadedFiles: 0,
  }),

  async mounted() {
    await this.loadBusiness();
    await this.getCategories();
    if (!(this.business.location && this.business.location.coordinates && this.business.location.coordinates.length > 0)) {
      this.geoLocate();
    } else {
      this.marker.position = {
        lng: this.business.location.coordinates[0],
        lat: this.business.location.coordinates[1]
      }
      this.panToMarker()
    }
  },
  watch: {
    searchKeywords(newVal) {
      this.setKeywords(newVal)
    }
  },

  methods: {
    required,
    validatePhone(event) {
      if (event) {
        this.business.phone = event.formatInternational;
        this.phoneError = !(event.isValid);
      }
    },
    email,
    requiredArray,
    url,
    setKeywords(val) {
      if (val) {
        if (val.includes(',')) {
          this.business.keywords.push(...(val.split(",")).filter((i) => i && i !== ''));
          this.business.keywords = this.business.keywords.filter((value, index, self) => {
            return self.indexOf(value) === index;
          })
          this.searchKeywords = "";
        }
      }
    },
    async setPlace(place) {
      let location = null
      let address = ''
      if (place && place.place_id) {
        location = place
        address = place.name + ' ' + place.formatted_address
      } else {
        try {
          const res = await fetch(`https://maps.google.com/maps/api/geocode/json?address=${encodeURIComponent(place.name)}&key=AIzaSyDQfr3V_JHN9CuguMFBCYV8ZLd7xEC4B-U`)
          const response = await res.json()
          if (response && response.results && response.results.length > 0) {
            location = response.results[0]
          }
          address = place.name
        } catch (e) {
          console.log(e)
        }
      }
      this.canGetAddress = false
      if (location) {
        this.currentPlace = location;
        this.business.address = address
        await this.getLatLng()
        this.setLocation()
        this.panToMarker();
      }
    },
    async loadBusiness() {
      if (!this.$route.query.id) return;
      this.$refs.form.showLoading(true, 'Loading Business Data...')
      this.isEdit = true;
      this.isView = !!this.$route.query.view
      this.business = await this.service.fetchOne(this.$route.query.id)
      this.uploadedMediaFiles = [...this.business.images.map((item) => {
        return {
          url: axios.defaults.baseURL + '/uploads/' + item,
          fileName: item,
        };
      })];
      if (this.business.phone) {
        console.log(this.business.phone)
        const phone = parsePhoneNumber(this.business.phone)
        console.log('phone', phone)
        if (phone && phone.isValid()) {
          this.phone = phone.nationalNumber
          this.countryCode = phone.country
        }
      }
      if (this.business.location && this.business.location.coordinates && this.business.location.coordinates.length > 0) {
        this.location = this.business.location.coordinates.join(',')
      }
      console.log('business', this.business)
      this.$refs.form.showLoading(false, '')
    },
    async getCategories() {
      this.loadingCategories = true
      this.categories = await this.categoryService.fetchAll()
      this.loadingCategories = false
    },
    preCheck(context) {
      this.showPhoneError = false
      let error = false
      if (!context.validate()) {
        error = true
      }
      if (this.phoneError) {
        this.phoneError = true
        this.showPhoneError = true
        error = true
      }
      return !error;
    },

    async submit(context) {
      if (this.preCheck(context)) {
        if (this.filesToBeDeleted && this.filesToBeDeleted.length > 0) {
          this.business.images = [...this.business.images.filter((item) => !this.filesToBeDeleted.includes(item))]
        }

        try {
          if (this.media && this.media.length > 0) {
            if (this.business.images && this.business.images.length > 0) {
              this.business.images = [
                ...this.business.images,
                ...(await uploadWithMessage(this.$refs.form, this.media, 'Uploading Images'))
              ];
            } else {
              this.business.images = [
                ...(await uploadWithMessage(this.$refs.form, this.media, 'Uploading Images'))
              ];
            }
            this.$refs.form.showLoading(false, '')
          }

          if (this.location) {
            this.business.location.type = 'Point'
            this.business.location.coordinates = this.location.split(',').map((i) => parseFloat(i))
          }

        } catch (e) {
          context.reportError({
            title: 'Error occurred',
            description: e && e.data && e.data.message ? e.data.message : e.toString() || 'Some error occurred. Try again later.',
          });
          return false
        }

        if (this.isEdit) {
          try {
            this.$refs.form.showLoading(true, 'Updating business...')
            await this.service.update(this.business._id, this.business)
            this.$refs.form.showLoading(false, '')
            this.$toast.success('Business updated successfully')
            return true
          } catch (e) {
            context.reportError({
              title: 'Error occurred while updating business',
              message: e && e.data && e.data.message ? e.data.message : e.toString() || 'Some error occurred. Try again later.',
            });
            return false
          }
        } else {
          try {
            this.$refs.form.showLoading(true, 'Creating business...')

            console.log('business: ', this.business)
            // await this.service.create(this.business)
            this.$refs.form.showLoading(false, '')
            this.$toast.success('Business created successfully')
            // return true
          } catch (e) {
            context.reportError({
              title: 'Error occurred while creating business',
              description: e && e.data && e.data.message ? e.data.message : e.toString() || 'Some error occurred. Try again later.',
            });
            return false
          }
        }
      }
    },

    clearForm() {
      this.media = []
      this.phone = null
      this.location = null
      this.business.images = []
      this.business.averageRating = 0
      this.loadBusiness()
    },
    geoLocate() {
      navigator.geolocation.getCurrentPosition((position) => {
        this.marker.position = {
          lng: position.coords.longitude,
          lat: position.coords.latitude,
        };
        this.setLocation()
        this.panToMarker();
        this.getAddress()
      }, (err) => {
        console.log(`ERROR(${err.code}): ${err.message}`);
      });
    },
    handleMarkerDrag(e) {
      this.marker.position = {
        lng: e.latLng.lng(),
        lat: e.latLng.lat()
      };
      this.setLocation()
    },
    panToMarker() {
      this.center = this.marker.position
    },
    setLocation() {
      this.location = this.marker.position.lng + ',' + this.marker.position.lat
      this.getAddress()
      this.setCoordinates()
    },
    handleMapClick(e) {
      if (!this.isView) {
        this.marker.position = {
          lng: e.latLng.lng(),
          lat: e.latLng.lat(),
        };
        this.setLocation()
      }
    },
    async getAddress() {
      if (this.canGetAddress) {
        try {
          await fetch('https://maps.googleapis.com/maps/api/geocode/json?latlng=' + this.marker.position.lat + ',' + this.marker.position.lng + '&key=AIzaSyDQfr3V_JHN9CuguMFBCYV8ZLd7xEC4B-U')
              .then(response => response.json())
              .then(res => {
                this.business.address = res.results[0].formatted_address
              })
        } catch (e) {
          console.log(e, 'Address error')
        }
      }
      this.canGetAddress = true
    },
    async getLatLng() {
      if (typeof this.currentPlace.geometry.location.lat === 'function') {
        this.marker.position = {
          lng: this.currentPlace.geometry.location.lng(),
          lat: this.currentPlace.geometry.location.lat(),
        };
      } else {
        this.marker.position = {
          lng: this.currentPlace.geometry.location.lng,
          lat: this.currentPlace.geometry.location.lat,
        };
      }
      this.setCoordinates()
    },
    setCoordinates() {
      if (!this.business.location) {
        this.business.location = {
          type: 'Points',
          coordinates: []
        }
      }
      this.business.location.coordinates.push(this.marker.position.lng)
      this.business.location.coordinates.push(this.marker.position.lat)
    },

    // media related methods
    removeMedia(media) {
      if (media.file) {
        const index = this.selectedMedia.indexOf(media.file)
        this.selectedMedia.splice(index, 1)
      }

      const indexMedia = this.media.indexOf(media)
      this.media.splice(indexMedia, 1)
      this.uploadedFiles = this.media.length
    },
    pickFile() {
      document.querySelector("#mediaPicker").click();
    },
    pickHandler() {
      this.uploadedFiles = 0
      this.handleMedia(this.selectedMedia)
      this.media = [...this.media]
    },
    async dropHandler(ev) {
      ev.preventDefault();
      this.uploadedFiles = 0
      await this.handleMedia(ev.dataTransfer.files)
      this.media = [...this.media]
    },
    async handleMedia(files) {
      // const allowedTypes = ['png', 'jpg', 'jpeg', 'gif','mp4'];
      for (let i = 0; i < files.length; i++) {
        if ((files[i].type.includes('image') && files[i].size < 5242880) || (files[i].type.includes('video') && files[i].size < 209715200)) {
          try {
            const fileData = {
              file: files[i],
              url: URL.createObjectURL(files[i]),
              uploaded: 0,
              isUploaded: false,
              name: files[i].name,
              type: files[i].type,
              size: files[i].size
            }
            fileData.uploaded = 100
            fileData.isUploaded = true
            this.media.push(fileData);
            this.uploadedFiles = this.media.length
          } catch (e) {
            this.$toast.error('Some error occurred. ' + files[i].name + ' cannot be selected.')
          }
        } else {
          this.$toast.error(files[i].type.includes('image') ? files[i].name + ' is too large. Max size allowed is 5 Mb' : files[i].name + ' is too large. Max size allowed is 200 Mb')
        }
      }
    },
    removeUploadedFiles(fileName, fileIndex) {
      this.uploadedMediaFiles.splice(fileIndex, 1)
      const index = this.business.images.findIndex((file) => file === fileName)
      if (index !== -1) {
        this.filesToBeDeleted.push(fileName)
      }
    },
  }
};
</script>

<style scoped>
p {
  font-weight: bold;
  text-align: left;
}

.mediaPrickerWrapper {
  border: 1px dashed #2177b0;
  border-radius: 8px;
}

.file-display {
  border: 1px solid #2177b0;
}

.contact-upload-dialog p, .contact-upload-dialog a {
  font-size: 14px;
}

.preview-image {
  width: 120px !important;
  height: 120px !important;
}

.remove-icon {
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  cursor: pointer !important;
}
</style>